// types.ts — the currency presentation contract (SM-443).
//
// Three fixed platform currencies, and one question this layer answers: given a
// currency CODE and an integer MINOR amount, what does the user see?
//
// The split that matters:
//   - VALUE is owned by src/lib/money.ts. Minor units in, minor units out,
//     BigInt for arithmetic. Nothing here ever adds, converts or rounds money.
//   - PRESENTATION is owned by this module. Name, symbol, icon, colour and
//     display decimals — everything that is a picture of a number, never the
//     number itself.
//
// Presentation is *configuration*: an operator sets a currency's display name,
// symbol, colour, icon and decimals in Backoffice, and the wire hands us those
// settings. Configuration can be absent, partial or stale, so every field here
// is optional on the way in and total on the way out — see resolve.ts, which
// fills the gaps from a frozen seed table.
//
// Deliberately NOT in this contract: wallet balances, GAME/CASH mode, rates,
// conversion, deposit rails. Those are other cards; this one only draws.

/** The three fixed platform currencies. Closed set — never widened at runtime. */
export const CURRENCY_CODES = ["RED", "GREEN", "GOLD"] as const;
export type CurrencyCode = (typeof CURRENCY_CODES)[number];

/**
 * How a currency's mark is drawn. Three kinds, in the order resolve.ts prefers
 * them:
 *
 *   image    — operator-supplied artwork. Optional on purpose: it is the one
 *              presentation field that can 404 long after it was configured,
 *              so <CurrencyAmount> degrades to `monogram` on load failure
 *              rather than leaving a broken-image box in a balance.
 *   brand    — the active brand's currency glyph (src/brand). Selectable but
 *              no currency's seeded default. GOLD's seed was `brand` for one
 *              release, and SM-488 is the bug that caused: one wallet, one
 *              registry row, and a different mark in each white-label product.
 *              An operator who genuinely wants the brand glyph can still
 *              choose it — per deployment, never per currency by default.
 *   monogram — a generated badge: the currency's symbol on its own colour.
 *              Deterministic from the code, so it looks the same in every app,
 *              every session and every test run. Not a seeded default either:
 *              it is the degrade path for artwork that fails to load, and the
 *              explicit choice for an operator who wants a letter pill.
 *
 * The seeded default for all three currencies is bundled coin artwork of the
 * `image` kind — platform constants, identical in every app and every brand.
 */
export type CurrencyIconSpec =
  | { kind: "image"; src: string; alt?: string }
  | { kind: "brand" }
  | { kind: "monogram" };

/**
 * A currency's presentation, fully resolved — every field present. This is what
 * a renderer consumes; nothing downstream needs to know which parts came from
 * configuration and which from the seed.
 */
export interface CurrencyPresentation {
  code: CurrencyCode;
  /** User-facing name: "Gold". Not a translation key — operator-authored copy. */
  displayName: string;
  /**
   * Short mark shown beside a figure and used as the monogram glyph. Kept to a
   * few characters: it sits inline in a balance pill and a table cell.
   */
  symbol: string;
  /** CSS colour for the mark. Never the ONLY signal — see colour independence below. */
  color: string;
  /** Display decimal places. Truncated to, never rounded to (money.ts's rule). */
  decimals: number;
  /**
   * Wire minor units per ONE displayed unit. 10 000 for all three currencies —
   * the epic's $1 currency unit, per SM-460: "one displayed whole RED, GREEN or
   * GOLD unit represents $1".
   *
   * SM-484 briefly gave GOLD 100 instead, to keep the legacy SPARK lens ("1
   * SPARK = 1 cent") that every existing balance was drawn with. That was the
   * wrong half to preserve: phase 03a had already relabelled PRICES to dollars
   * via formatPrice, so wire 5000 rendered "$0.50" as a price and
   * "⚡50.00" as an amount on the same screen. 03a's own pricing_policy seed
   * reads "SPARK display retired"; this field is where that retirement lands.
   *
   * A DISPLAY divisor, exactly like `decimals` — it moves the decimal point
   * and nothing else. The wire integer never moves ($1.00 = 10000 in every
   * currency, before and after), so no value is rescaled and G2 holds. What
   * moved is only how many digits sit left of the point.
   */
  unitMinor: number;
  icon: CurrencyIconSpec;
}

/**
 * The same shape as configuration supplies it: any field may be missing, and
 * the whole entry for a currency may be missing. Feed it straight from the API
 * — resolve.ts is what makes it total.
 */
export type CurrencyPresentationInput = Partial<Omit<CurrencyPresentation, "code">>;

/** Operator-configured presentation for some or all currencies. */
export type CurrencyPresentationSettings = Partial<Record<CurrencyCode, CurrencyPresentationInput>>;

/** Narrowing guard for wire values. Unknown codes are rejected, never coerced. */
export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === "string" && (CURRENCY_CODES as readonly string[]).includes(value);
}

/**
 * Thrown by resolveCurrencyPresentation for a code outside the fixed set.
 *
 * Rejecting is the point: a currency the platform does not have is a bug in the
 * caller or a corrupted payload, and silently drawing it as GOLD would put a
 * wrong unit next to a real balance. Callers that legitimately hold an unknown
 * code (rendering arbitrary wire data) use tryResolveCurrencyPresentation and
 * decide what to draw instead.
 */
export class UnknownCurrencyError extends Error {
  readonly code: string;
  constructor(code: string) {
    super(`Unknown currency code: ${JSON.stringify(code)}. Expected one of ${CURRENCY_CODES.join(", ")}.`);
    this.name = "UnknownCurrencyError";
    this.code = code;
  }
}
